import { useEffect } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useNavigation, useRoute } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import type { RouteProp } from '@react-navigation/native'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { LockKeyhole } from 'lucide-react-native'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import type { AdminAuthStackParamList } from '../../../navigation/types'
import { Button, InlineAlert, PasswordField } from '@chillout/ui'
import { AdminAuthLayout } from './AdminAuthLayout'
import { AuthScreenHeader } from './AuthScreenHeader'
import { BackToSignInLink } from './BackToSignInLink'
import { createPasswordSchema } from './authSchemas'

export function AdminCreatePasswordScreen() {
  const { t } = useTranslation()
  const navigation = useNavigation<NativeStackNavigationProp<AdminAuthStackParamList>>()
  const route = useRoute<RouteProp<AdminAuthStackParamList, 'CreatePassword'>>()

  const { control, handleSubmit, setError, clearErrors, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(createPasswordSchema(t)),
    defaultValues: { password: '', confirmPassword: '' },
  })

  useEffect(() => {
    if (!route.params?.isRecovery) {
      navigation.replace('ResetLinkError')
    }
  }, [navigation, route.params?.isRecovery])

  const onSubmit = handleSubmit(async ({ password }) => {
    try {
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        const isSamePassword = error.code === 'same_password'
        if (!isSamePassword) reportUnexpectedError(error)
        setError('root', {
          message: isSamePassword ? t('adminAuth.samePasswordError') : t('adminAuth.updatePasswordError'),
        })
        return
      }

      await supabase.rpc('log_admin_auth_event', { p_event_type: 'password_changed' })
      await supabase.auth.signOut()
      navigation.navigate('SignIn')
    } catch (err) {
      reportUnexpectedError(err)
      setError('root', { message: t('adminAuth.updatePasswordError') })
    }
  })

  return (
    <AdminAuthLayout>
      <AuthScreenHeader
        Icon={LockKeyhole}
        iconAccessibilityLabel={t('adminAuth.createPasswordIconAccessibilityLabel')}
        title={t('adminAuth.createPasswordTitle')}
        subtitle={t('adminAuth.createPasswordSubtitle')}
      />
      <View className="mt-8 w-full sm:mt-10">
        <Controller
          control={control}
          name="password"
          render={({ field, fieldState }) => (
            <PasswordField
              label={t('adminAuth.newPasswordLabel')}
              placeholder={t('adminAuth.newPasswordPlaceholder')}
              showPasswordAccessibilityLabel={t('adminAuth.showPasswordAccessibilityLabel')}
              hidePasswordAccessibilityLabel={t('adminAuth.hidePasswordAccessibilityLabel')}
              helperText={t('adminAuth.newPasswordHelperText')}
              value={field.value}
              onChangeText={(v) => { field.onChange(v); clearErrors('root') }}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
        <View className="mt-6">
          <Controller
            control={control}
            name="confirmPassword"
            render={({ field, fieldState }) => (
              <PasswordField
                label={t('adminAuth.confirmPasswordLabel')}
                placeholder={t('adminAuth.confirmPasswordPlaceholder')}
                showPasswordAccessibilityLabel={t('adminAuth.showPasswordAccessibilityLabel')}
                hidePasswordAccessibilityLabel={t('adminAuth.hidePasswordAccessibilityLabel')}
                value={field.value}
                onChangeText={(v) => { field.onChange(v); clearErrors('root') }}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />
        </View>
        {errors.root ? (
          <View className="mt-6">
            <InlineAlert message={errors.root.message ?? ''} />
          </View>
        ) : null}
        <Button
          label={t('adminAuth.updatePasswordButton')}
          loadingLabel={t('adminAuth.updatingPasswordButton')}
          loading={isSubmitting}
          onPress={onSubmit}
          className={errors.root ? 'mt-6' : 'mt-8'}
        />
        <BackToSignInLink
          onPress={() => navigation.navigate('SignIn')}
          className="mt-6"
        />
      </View>
    </AdminAuthLayout>
  )
}